// El ejecutor de las herramientas de Videos cortos (ADR-2026-10-30-videos-cortos-con-el-agente):
// valida lo que escribió el modelo, lo aplica con las MISMAS acciones que usan los botones
// (`usePlanDeVideos`) y le cuenta en texto qué pasó y qué falta. Puro salvo lo que delega en
// `acciones`.
import type { ResultadoDeHerramientaDePantalla } from '../../../../../chat-atlas';
import type { ResultadoDeEscritura } from '../escritorDeGuiones';
import type { ResultadoDeGeneracion } from '../generarVideos';
import {
  costoPorLanzar,
  MAX_INDICACION,
  MAX_LOCUCION,
  MAX_NOTAS,
  MAX_PRODUCTOS,
  MAX_PROMPT,
  MAX_TITULO,
  puedeRehacerSoloLaVoz,
  queFalta,
  videosPorLanzar,
  vozDelProducto,
  type FiltroDeProductos,
  type PlanDeVideos,
  type ProductoParaLaTanda,
  type ProductosSumados,
} from '../plan';
import type { VozDeVideo, VozDelEstudio } from '../videosCortosApi';
import { nombreDeVoz } from './describir';
import { NOMBRES_HERRAMIENTAS_DE_VIDEOS_CORTOS } from './herramientas';

/** Lo que la sesión sabe hacer (las acciones de `usePlanDeVideos` que el agente usa). */
export interface AccionesDeVideosCortos {
  leerPlan: () => PlanDeVideos;
  setTitulo: (t: string) => void;
  setIndicacion: (i: string) => void;
  setSubtitulos: (s: boolean) => void;
  setVoz: (varianteId: string | null, voz: VozDeVideo | null) => void;
  setNotas: (varianteId: string, notas: string) => void;
  editarGuion: (varianteId: string, parcial: { prompt?: string; locucion?: string }) => void;
  escribir: (solo?: FiltroDeProductos, indicacion?: string, aunqueEstenEscritos?: boolean) => Promise<ResultadoDeEscritura>;
  /** Suelta un guion editado a mano para que el escritor lo pueda reescribir. */
  desfijar: (varianteId: string) => void;
  marcarRehacer: (ids?: string[], o?: { soloVoz?: boolean }) => { marcados: string[] };
  sumarProductos: (productos: ProductoParaLaTanda[]) => ProductosSumados;
  quitarProductos: (ids: string[]) => { quitados: string[]; enVuelo: string[] };
  /** Los productos del catálogo de la marca, listos para la tanda. */
  buscarProductos: (ids: string[]) => Promise<{ encontrados: ProductoParaLaTanda[]; noEncontrados: string[] }>;
  pedirGenerar: () => Promise<ResultadoDeGeneracion>;
}

export interface ContextoDeVideosCortos {
  plan: PlanDeVideos;
  voces: VozDelEstudio[];
}

const ok = (texto: string): ResultadoDeHerramientaDePantalla => ({ content: texto });
const error = (texto: string): ResultadoDeHerramientaDePantalla => ({ content: texto, is_error: true });
const CIERRE = 'Para ver el lote con los ids: ver_pantalla.';

const str = (v: unknown, max = 10_000): string => (typeof v === 'string' ? v.trim().slice(0, max) : '');
const listaDeIds = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : undefined);
const filtroDe = (v: unknown): FiltroDeProductos | undefined => {
  const o = (v ?? undefined) as Record<string, unknown> | undefined;
  const productos = o && typeof o === 'object' ? listaDeIds(o.productos) : undefined;
  return productos?.length ? { productos } : undefined;
};
const normalizar = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();

/**
 * Lo que el modelo escribió como voz → la voz del plan: `auto`, `sin` (null), `mujer`/`hombre`, o una voz de la lista
 * por id o por nombre (sin acentos ni mayúsculas). `undefined` = no se reconoció. Puro.
 */
export function resolverVoz(pedida: unknown, voces: VozDelEstudio[]): VozDeVideo | null | undefined {
  const s = normalizar(str(pedida, 120));
  if (!s) return undefined;
  if (s === 'auto' || s === 'automatica') return 'auto';
  if (s === 'sin' || s === 'sin voz' || s === 'ninguna' || s === 'null') return null;
  if (s === 'mujer' || s === 'hombre') return s;
  const v = voces.find((x) => x.id === str(pedida)) ?? voces.find((x) => normalizar(x.id) === s) ?? voces.find((x) => normalizar(x.nombre) === s) ?? voces.find((x) => normalizar(x.nombre.split(' - ')[0] ?? '') === s);
  return v?.id;
}

const resumen = (plan: PlanDeVideos): string => {
  const faltas = queFalta(plan);
  const n = videosPorLanzar(plan).length;
  const escritos = plan.productos.filter((p) => !!p.guion).length;
  return `${plan.productos.length} producto${plan.productos.length === 1 ? '' : 's'}, ${escritos} con guion; Crear lanza ${n} video${n === 1 ? '' : 's'} (${costoPorLanzar(plan)} créditos).${faltas.length ? ` Falta: ${faltas.join('; ')}.` : ' No falta nada.'}`;
};

export async function ejecutarHerramientaDeVideosCortos(
  nombre: string,
  input: unknown,
  ctx: ContextoDeVideosCortos,
  acciones: AccionesDeVideosCortos,
): Promise<ResultadoDeHerramientaDePantalla> {
  if (!NOMBRES_HERRAMIENTAS_DE_VIDEOS_CORTOS.includes(nombre)) return error(`La herramienta ${nombre} no es de Videos cortos.`);
  const o = (input ?? {}) as Record<string, unknown>;
  const nombreDe = (id: string) => acciones.leerPlan().productos.find((p) => p.varianteId === id)?.nombre ?? id;
  try {
    switch (nombre) {
      case 'escribir_guiones': {
        const solo = filtroDe(o.solo);
        const plan = acciones.leerPlan();
        const ajenos = (solo?.productos ?? []).filter((id) => !plan.productos.some((p) => p.varianteId === id));
        if (ajenos.length) return error(`Estos ids no están en el lote: ${ajenos.join(', ')}. ${CIERRE}`);
        const indicacion = str(o.indicacion, MAX_INDICACION) || undefined;
        // Reescribir ESTOS productos (nombrados) pisa lo editado a mano: es lo que la persona pidió
        // con Reescribir (ADR-2026-10-31-videos-cortos-reescribir-pisa-lo-editado). Sin nombrarlos, un
        // fijado no se toca.
        if (o.aunque_esten_escritos === true && solo?.productos?.length) {
          for (const p of plan.productos) if (p.fijado && solo.productos.includes(p.varianteId)) acciones.desfijar(p.varianteId);
        }
        const r = await acciones.escribir(solo, indicacion, o.aunque_esten_escritos === true);
        const fijados = acciones.leerPlan().productos.filter((p) => p.fijado && (!solo || solo.productos!.includes(p.varianteId))).map((p) => p.nombre);
        if (r.escritos === 0 && r.fallidos.length === 0) {
          return ok(`No había guiones por escribir${fijados.length ? ` (fijados: ${fijados.join(', ')})` : ''}${!o.aunque_esten_escritos ? '; para reescribir los que ya tienen guion, aunque_esten_escritos: true' : ''}. ${resumen(acciones.leerPlan())}`);
        }
        const despues = acciones.leerPlan();
        return ok(
          `Escritos: ${r.escritos}.${r.fallidos.length ? ` Fallaron: ${r.fallidos.map((f) => `${f.producto} (${f.motivo})`).join('; ')}.` : ''}${fijados.length ? ` No se tocaron los fijados: ${fijados.join(', ')}.` : ''} Los guiones ya están en las tarjetas: no los repitas. Ahora elegí la voz de cada uno con elegir_voz si todavía no lo hiciste. ${resumen(despues)} ${CIERRE}`,
        );
      }
      case 'ver_guiones': {
        const solo = filtroDe(o.solo);
        const plan = acciones.leerPlan();
        const lista = plan.productos.filter((p) => !solo || solo.productos!.includes(p.varianteId));
        if (lista.length === 0) return error(`No hay productos con esos ids. ${CIERRE}`);
        const partes = lista.map((p) => {
          const g = p.guion;
          const cabecera = `## ${p.nombre} (id=${p.varianteId}) · ${p.estado}${p.fijado ? ' · fijado' : ''} · voz: ${nombreDeVoz(vozDelProducto(plan, p), ctx.voces)}${p.motivo ? ` · ${p.motivo}` : ''}`;
          if (!g) return `${cabecera}\n(sin guion)`;
          return [
            cabecera,
            `Público: ${g.publico ?? '?'} · Rubro: ${g.rubro ?? '?'}`,
            `Idea: ${g.idea ?? '(sin idea)'}`,
            `Tomas:\n${g.escenas.map((e, i) => `  ${i + 1}. ${e}`).join('\n') || '  (sin tomas)'}`,
            `Locución: ${g.locucion?.texto ?? '(sin locución)'}`,
            `Prompt:\n${g.prompt}`,
          ].join('\n');
        });
        return ok(partes.join('\n\n'));
      }
      case 'editar_guion': {
        const id = str(o.producto_id);
        const plan = acciones.leerPlan();
        const producto = plan.productos.find((p) => p.varianteId === id);
        if (!producto) return error(`No hay un producto "${id}" en el lote. ${CIERRE}`);
        if (producto.estado === 'en_cola' || producto.estado === 'generando') return error(`${producto.nombre} se está creando: esperá a que salga.`);
        const parcial: { prompt?: string; locucion?: string } = {};
        if (typeof o.prompt === 'string') parcial.prompt = o.prompt.trim().slice(0, MAX_PROMPT);
        if (typeof o.locucion === 'string') parcial.locucion = o.locucion.trim().slice(0, MAX_LOCUCION);
        if (parcial.prompt === undefined && parcial.locucion === undefined) return error('editar_guion necesita prompt o locucion.');
        if (parcial.prompt !== undefined && !parcial.prompt && !producto.guion) return error('Un prompt vacío no sirve: escribilo entero, o pedilo con escribir_guiones.');
        acciones.editarGuion(id, parcial);
        const despues = acciones.leerPlan();
        const p2 = despues.productos.find((p) => p.varianteId === id)!;
        const avisoVideo = producto.estado === 'listo' ? ' El video que ya salió sigue: si la persona quiere filmarlo de nuevo con este guion, crear_videos con rehacer.' : '';
        const avisoVoz = p2.guion && !p2.guion.locucion && vozDelProducto(despues, p2) !== null ? ' OJO: quedó con voz y sin locución; sacale la voz (elegir_voz "sin") o escribí la locución.' : '';
        return ok(`Guion de ${producto.nombre} cambiado y fijado (${[parcial.prompt !== undefined ? 'prompt' : '', parcial.locucion !== undefined ? 'locución' : ''].filter(Boolean).join(' y ')}).${avisoVideo}${avisoVoz} ${resumen(despues)}`);
      }
      case 'elegir_voz': {
        const lista = Array.isArray(o.asignaciones) ? (o.asignaciones as Array<Record<string, unknown>>) : [];
        if (lista.length === 0) return error('elegir_voz necesita al menos una asignación.');
        const hechas: string[] = [];
        const rechazadas: string[] = [];
        for (const a of lista) {
          const voz = resolverVoz(a.voz, ctx.voces);
          if (voz === undefined) {
            rechazadas.push(`la voz "${str(a.voz, 60)}" no está en la lista (ver_pantalla la tiene)`);
            continue;
          }
          const id = str(a.producto_id);
          if (!id) {
            acciones.setVoz(null, voz);
            hechas.push(`el lote: ${nombreDeVoz(voz, ctx.voces)}`);
            continue;
          }
          const producto = acciones.leerPlan().productos.find((p) => p.varianteId === id);
          if (!producto) {
            rechazadas.push(`no hay un producto "${id}"`);
            continue;
          }
          if (producto.estado === 'en_cola' || producto.estado === 'generando') {
            rechazadas.push(`${producto.nombre} se está creando`);
            continue;
          }
          acciones.setVoz(id, voz);
          hechas.push(`${producto.nombre}: ${nombreDeVoz(voz, ctx.voces)}`);
        }
        const despues = acciones.leerPlan();
        const porReescribir = queFalta(despues).find((f) => f.includes('locución'));
        return ok(
          `${hechas.length ? `Voces: ${hechas.join('; ')}.` : 'No se cambió ninguna voz.'}${rechazadas.length ? ` Rechazado: ${rechazadas.join('; ')}.` : ''}${porReescribir ? ` ${porReescribir}: llamá escribir_guiones.` : ''} ${resumen(despues)}`,
        );
      }
      case 'configurar_tanda': {
        const hechas: string[] = [];
        const rechazadas: string[] = [];
        if (typeof o.titulo === 'string') {
          const t = str(o.titulo, MAX_TITULO);
          if (t) {
            acciones.setTitulo(t);
            hechas.push('título');
          } else rechazadas.push('título vacío');
        }
        if (typeof o.subtitulos === 'boolean') {
          acciones.setSubtitulos(o.subtitulos);
          hechas.push(`subtítulos ${o.subtitulos ? 'sí' : 'no'}`);
        }
        if (typeof o.indicacion === 'string') {
          acciones.setIndicacion(o.indicacion.trim().slice(0, MAX_INDICACION));
          hechas.push(o.indicacion.trim() ? 'indicación general (vale para las próximas escrituras; para aplicarla a lo escrito, escribir_guiones con aunque_esten_escritos)' : 'indicación general sacada');
        }
        if (Array.isArray(o.notas)) {
          for (const n of o.notas as Array<Record<string, unknown>>) {
            const id = str(n.producto_id);
            const producto = acciones.leerPlan().productos.find((p) => p.varianteId === id);
            if (!producto) {
              rechazadas.push(`no hay un producto "${id}"`);
              continue;
            }
            acciones.setNotas(id, str(n.notas, MAX_NOTAS));
            hechas.push(`notas de ${producto.nombre}`);
          }
        }
        if (hechas.length === 0 && rechazadas.length === 0) return error('configurar_tanda no recibió nada para cambiar.');
        return ok(`${hechas.length ? `Hecho: ${hechas.join('; ')}.` : ''}${rechazadas.length ? ` Rechazado: ${rechazadas.join('; ')}.` : ''} ${resumen(acciones.leerPlan())}`);
      }
      case 'sumar_productos_a_la_tanda': {
        const ids = [...new Set(listaDeIds(o.product_ids) ?? [])];
        if (ids.length === 0) return error('sumar_productos_a_la_tanda necesita al menos un product_id.');
        const { encontrados, noEncontrados } = await acciones.buscarProductos(ids);
        if (encontrados.length === 0) return error(`No encontré ${ids.length === 1 ? 'ese producto' : 'esos productos'} en el catálogo de la marca. Si mandó fotos de un producto nuevo, crealo primero con agregar_productos.`);
        const r = acciones.sumarProductos(encontrados);
        const nombre = (id: string) => encontrados.find((p) => p.varianteId === id)?.nombre ?? id;
        const partes = [
          r.sumados.length ? `Sumé ${r.sumados.map(nombre).join(', ')}.` : 'No sumé nada.',
          r.yaEstaban.length ? `Ya estaban: ${[...new Set(r.yaEstaban)].map(nombre).join(', ')}.` : '',
          noEncontrados.length ? `No están en el catálogo de la marca: ${noEncontrados.join(', ')}.` : '',
          r.sinLugar.length ? `No entraron por el tope de ${MAX_PRODUCTOS} productos: ${r.sinLugar.map(nombre).join(', ')}.` : '',
          r.sumados.length ? `Escribí sus guiones con escribir_guiones y solo.productos = ${JSON.stringify(r.sumados)}.` : '',
          resumen(acciones.leerPlan()),
          CIERRE,
        ];
        return ok(partes.filter(Boolean).join(' '));
      }
      case 'quitar_productos_de_la_tanda': {
        const ids = [...new Set(listaDeIds(o.product_ids) ?? [])];
        if (ids.length === 0) return error('quitar_productos_de_la_tanda necesita al menos un product_id.');
        const nombres = new Map(acciones.leerPlan().productos.map((p) => [p.varianteId, p.nombre]));
        const r = acciones.quitarProductos(ids);
        const desconocidos = ids.filter((id) => !nombres.has(id));
        return ok(
          `${r.quitados.length ? `Saqué ${r.quitados.map((id) => nombres.get(id) ?? id).join(', ')}.` : 'No saqué nada.'}${r.enVuelo.length ? ` ${r.enVuelo.map((id) => nombres.get(id) ?? id).join(', ')} se está${r.enVuelo.length === 1 ? '' : 'n'} creando: no se puede sacar.` : ''}${desconocidos.length ? ` No están en el lote: ${desconocidos.join(', ')}.` : ''} ${resumen(acciones.leerPlan())}`,
        );
      }
      case 'crear_videos': {
        const rehacer = listaDeIds(o.rehacer);
        // Rehacer sólo la voz (ADR-2026-10-31-videos-cortos-rehacer-solo-la-voz): se valida ANTES de marcar nada.
        const soloVoz = listaDeIds(o.solo_voz);
        if (soloVoz?.length) {
          const vivos = acciones.leerPlan().productos;
          const sinFuente = soloVoz.filter((id) => {
            const p = vivos.find((x) => x.varianteId === id);
            return !p || !puedeRehacerSoloLaVoz(p);
          });
          if (sinFuente.length) {
            return error(`A ${sinFuente.map(nombreDe).join(', ')} no se le puede rehacer sólo la voz (todavía no salió o es un video de antes): rehacelo entero con \`rehacer\` (12 créditos).`);
          }
          acciones.marcarRehacer(soloVoz, { soloVoz: true });
        }
        if (rehacer?.length) {
          const r = acciones.marcarRehacer(rehacer);
          const noMarcados = rehacer.filter((id) => !r.marcados.includes(id));
          if (noMarcados.length) return error(`Sólo se puede rehacer un video que ya salió y tiene guion. No aplica a: ${noMarcados.map(nombreDe).join(', ')}.`);
        }
        const plan = acciones.leerPlan();
        const faltas = queFalta(plan);
        if (faltas.length) return error(`Todavía no se puede crear: ${faltas.join('; ')}. ${CIERRE}`);
        const n = videosPorLanzar(plan).length;
        if (n === 0) return ok('No hay videos por crear: todo lo escrito ya se creó o se está creando. Para volver a filmar uno que salió, crear_videos con rehacer.');
        const costo = costoPorLanzar(plan);
        const r = await acciones.pedirGenerar();
        if (!r.ok) return error(`No se crearon los videos: ${r.error}${r.sinCreditos ? ' (sin créditos suficientes)' : ''}.`);
        return ok(`Se lanzaron ${r.lanzados} video${r.lanzados === 1 ? '' : 's'} (${costo} créditos). Cada uno tarda unos 5 minutos y se ve en su tarjeta al salir; no hace falta que preguntes cómo van. ${CIERRE}`);
      }
      default:
        return error(`La herramienta ${nombre} no es de Videos cortos.`);
    }
  } catch (err) {
    return error(`Falló ${nombre}: ${err instanceof Error ? err.message : String(err)}`);
  }
}
